/**
 * HR/Owner — ดูประวัติการเข้างานของพนักงานรายคนทั้งช่วง
 * (มา/ขาด/ลา + เข้า-ออกกี่โมงรายวัน + สาย). เลือกพนักงาน + ช่วงเวลา.
 * ใช้ AttendanceTimeline ร่วมกับหน้า "ของฉัน".
 *
 * ด้านบนมีการ์ด "ดึงรายงานทั้งบริษัท" — ไฟล์ CSV ทุกคน × ทุกวันของงวดที่เลือก
 * สำหรับเอาไปกระทบยอดกับที่จดมือก่อนคิดเงินเดือน.
 */
import { useState } from 'react';
import { Download, History } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { currentCyclePeriod, isCalendarMonthCycle, payCycleRange, prevPeriod } from '@payroll/shared';
import {
  useEmployees,
  useEmployeeAttendance,
  fetchAttendanceExport,
  useManualAttendanceEntry,
  useOverrideAttendanceDay,
  useAmendApprovedOt,
  useTimeSettings,
  type MyAttendanceDay,
  type OtOnDay,
} from '../../hooks';
import { buildAttendanceCsv } from '../../lib/attendance-report';
import { thaiDateShort } from '../../lib/changelog';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import { useAuthStore } from '../../lib/store';
import {
  AttendanceTimeline,
  AttendanceRangePresets,
  leaveLabel,
  rangeForPreset,
  rangeLabel,
  todayBkk,
  weekRange,
  type RangePreset,
  type AmendOtInput,
} from '../../components/domain/AttendanceTimeline';

/** 6 งวดล่าสุด (YYYY-MM) — งวดปัจจุบันอยู่บนสุด */
function lastPeriods(n = 6): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const t = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}
function periodLabel(p: string): string {
  const y = Number(p.slice(0, 4));
  const m = Number(p.slice(5, 7));
  return new Date(y, m - 1, 1).toLocaleDateString('th-TH', { month: 'long', year: 'numeric' });
}

/** 6 รอบจ่ายล่าสุด (รอบที่กำลังเดินอยู่บนสุด) — คืนเป็น "งวดที่วันปิดตกอยู่" */
function cyclePeriods(cutoffDay: number, today: string, n = 6): string[] {
  const out: string[] = [];
  let p = currentCyclePeriod(cutoffDay, today);
  for (let i = 0; i < n; i++) {
    out.push(p);
    p = prevPeriod(p);
  }
  return out;
}

/** เพดานฝั่ง server (attendance-view.ts MAX_EXPORT_DAYS) — บอกผู้ใช้ก่อนจะยิงไปโดนปฏิเสธ */
const MAX_EXPORT_DAYS = 92;

const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400_000) + 1;

type ExportMode = 'day' | 'week' | 'cycle' | 'period' | 'custom';

/** ป้ายช่วง — วันเดียวไม่ต้องขึ้น "11 ก.ย. – 11 ก.ย." ให้ดูเหมือนมีสองวัน */
function spanLabel(from: string, to: string): string {
  if (from !== to) return rangeLabel(from, to);
  return new Intl.DateTimeFormat('th-TH', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok',
  }).format(new Date(`${from}T00:00:00+07:00`));
}

/** 8 สัปดาห์ล่าสุด (จันทร์–อาทิตย์) ไล่จากสัปดาห์นี้ย้อนหลัง */
function lastWeeks(n = 8) {
  return Array.from({ length: n }, (_, i) => weekRange(-i));
}

/** การ์ดดึงรายงานเวลาเข้า-ออกทั้งบริษัทเป็นไฟล์ CSV (เปิดใน Excel ได้เลย) */
function AttendanceExportCard({ cutoffDay }: { cutoffDay: number }) {
  const periods = lastPeriods();
  const today = todayBkk();
  // บริษัทที่ปิดงวดตรงสิ้นเดือนอยู่แล้ว "รอบจ่าย" กับ "เดือนปฏิทิน" คืออันเดียวกัน
  // → ไม่ต้องมีปุ่มซ้ำให้เลือกผิด
  const cycleAvailable = !isCalendarMonthCycle(cutoffDay, today.slice(0, 7));

  const [mode, setMode] = useState<ExportMode>(cycleAvailable ? 'cycle' : 'period');
  const [period, setPeriod] = useState<string>(periods[0]!);
  const [cyclePeriod, setCyclePeriod] = useState<string>(() => currentCyclePeriod(cutoffDay, today));
  const [custom, setCustom] = useState(() => payCycleRange(cutoffDay, currentCyclePeriod(cutoffDay, today)));
  const [day, setDay] = useState<string>(today);
  const [weekFrom, setWeekFrom] = useState<string>(() => weekRange(0).from);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const weeks = lastWeeks();
  const week = weeks.find((w) => w.from === weekFrom) ?? weeks[0]!;
  const range =
    mode === 'cycle' ? payCycleRange(cutoffDay, cyclePeriod)
      : mode === 'custom' ? custom
      : mode === 'week' ? week
      // รายวัน = ช่วงวันเดียว (from = to) — API รับ from/to อยู่แล้ว ไม่ต้องเพิ่มเส้นใหม่
      : mode === 'day' ? { from: day, to: day }
      : null;
  const tooLong = range ? daysBetween(range.from, range.to) > MAX_EXPORT_DAYS : false;
  const backwards = range ? range.from > range.to : false;

  async function handleDownload() {
    if (busy) return;
    if (backwards) return toast.error('วันเริ่มต้องไม่เกินวันสิ้นสุด');
    if (tooLong) return toast.error(`ดึงได้ครั้งละไม่เกิน ${MAX_EXPORT_DAYS} วัน — แบ่งดึงเป็นช่วงย่อย`);
    setBusy(true);
    try {
      // โหมดเดือนปฏิทิน = 1–สิ้นเดือนเสมอ (บริษัทรอบ 26-25 เลือกโหมด "รอบจ่าย" แยกอยู่แล้ว)
      const data = await fetchAttendanceExport(range ?? period, { calendar: true });
      if (data.rows.length === 0) {
        toast.error('ช่วงที่เลือกยังไม่มีข้อมูลพนักงาน');
        return;
      }
      const label = range ? spanLabel(range.from, range.to) : periodLabel(period);
      const csv = buildAttendanceCsv(data, label);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = !range
        ? `เวลาเข้าออก-${period}.csv`
        : range.from === range.to
          ? `เวลาเข้าออก-${range.from}.csv`
          : `เวลาเข้าออก-${range.from}_ถึง_${range.to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      if (!data.payroll_comparable) {
        toast.success('ดึงตามช่วงวันที่แล้ว — ไฟล์นี้มีเฉพาะตัวเลขที่นับจากสแกนจริง (ไม่มีคอลัมน์ฝั่งระบบคิดเงิน)');
      } else if (!data.reconciled) {
        toast.error('งวดนี้ยังไม่ได้รัน "สรุปเวลาเข้างาน" — คอลัมน์ฝั่งระบบคิดเงินจะยังว่าง');
      } else if (data.summary_stale) {
        toast.error('ตัวเลขฝั่ง "ระบบคิดเงิน" ยังไม่ตามข้อมูลล่าสุด — ให้รัน "สรุปเวลาเข้างาน" ใหม่ก่อนปิดงวด');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ดึงรายงานไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }

  const modes: Array<{ id: ExportMode; label: string }> = [
    { id: 'day', label: 'รายวัน' },
    { id: 'week', label: 'รายสัปดาห์' },
    ...(cycleAvailable ? [{ id: 'cycle' as const, label: `รอบจ่าย (${Number(payCycleRange(cutoffDay, cyclePeriod).from.slice(8, 10))}–${cutoffDay})` }] : []),
    { id: 'period', label: 'เดือนปฏิทิน' },
    { id: 'custom', label: 'กำหนดวันเอง' },
  ];

  return (
    <div className="card p-4 mb-3">
      <div className="font-semibold">ดึงรายงานเวลาเข้า-ออกทั้งบริษัท</div>
      <p className="text-sm text-gray-500 mt-0.5 mb-3">
        ไฟล์เดียวมีทุกคน × ทุกวันของช่วงที่เลือก (เข้ากี่โมง ออกกี่โมง สายกี่นาที ลา/ขาด/OT)
        พร้อมสรุปรายคน — เปิดใน Excel ได้เลย
      </p>

      <div className="flex gap-1 overflow-x-auto mb-2">
        {modes.map((m) => (
          <button
            key={m.id}
            onClick={() => setMode(m.id)}
            className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${
              mode === m.id ? 'bg-brand-100 text-brand-700 font-medium' : 'bg-white border border-gray-200 text-gray-600'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'custom' && (
        <div className="flex flex-col sm:flex-row gap-2 mb-2">
          <label className="flex-1">
            <span className="label">ตั้งแต่วันที่</span>
            <input
              type="date"
              value={custom.from}
              onChange={(e) => e.target.value && setCustom((r) => ({ ...r, from: e.target.value }))}
              className="input w-full"
            />
          </label>
          <label className="flex-1">
            <span className="label">ถึงวันที่</span>
            <input
              type="date"
              value={custom.to}
              onChange={(e) => e.target.value && setCustom((r) => ({ ...r, to: e.target.value }))}
              className="input w-full"
            />
          </label>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        {mode === 'cycle' && (
          <select value={cyclePeriod} onChange={(e) => setCyclePeriod(e.target.value)} className="input sm:max-w-xs">
            {cyclePeriods(cutoffDay, today).map((p) => {
              const r = payCycleRange(cutoffDay, p);
              return <option key={p} value={p}>{rangeLabel(r.from, r.to)}</option>;
            })}
          </select>
        )}
        {mode === 'day' && (
          <input
            type="date"
            value={day}
            max={today}
            onChange={(e) => e.target.value && setDay(e.target.value)}
            className="input sm:max-w-xs"
          />
        )}
        {mode === 'week' && (
          <select value={weekFrom} onChange={(e) => setWeekFrom(e.target.value)} className="input sm:max-w-xs">
            {weeks.map((w, i) => (
              <option key={w.from} value={w.from}>
                {i === 0 ? 'สัปดาห์นี้' : i === 1 ? 'สัปดาห์ที่แล้ว' : rangeLabel(w.from, w.to)}
                {i <= 1 ? ` (${rangeLabel(w.from, w.to)})` : ''}
              </option>
            ))}
          </select>
        )}
        {mode === 'period' && (
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="input sm:max-w-xs">
            {periods.map((p) => (
              <option key={p} value={p}>{periodLabel(p)}</option>
            ))}
          </select>
        )}
        <button onClick={handleDownload} disabled={busy || tooLong || backwards} className="btn-primary whitespace-nowrap">
          <Download className="w-4 h-4" /> {busy ? 'กำลังดึง…' : 'ดาวน์โหลดไฟล์'}
        </button>
      </div>

      {range && (
        <p className="text-xs text-gray-500 mt-2">
          ช่วงที่จะดึง: {range.from === range.to ? spanLabel(range.from, range.to) : `${rangeLabel(range.from, range.to)} (${daysBetween(range.from, range.to)} วัน)`}
          {' · '}ไฟล์นี้ไม่มีคอลัมน์ฝั่ง "ระบบคิดเงิน" เพราะระบบคิดเงินสรุปเป็นงวดรายเดือน (1 ถึงสิ้นเดือน) คนละช่วงวันที่กัน
        </p>
      )}
      {backwards && <p className="text-xs text-amber-600 mt-1">วันเริ่มต้องไม่เกินวันสิ้นสุด</p>}
      {tooLong && (
        <p className="text-xs text-amber-600 mt-1">
          ช่วงยาวเกิน {MAX_EXPORT_DAYS} วัน — แบ่งดึงเป็นช่วงย่อยแล้วต่อกันใน Excel
        </p>
      )}
    </div>
  );
}

interface EmpOpt {
  emp_code: string;
  first_name?: string | null;
  last_name?: string | null;
  nickname?: string | null;
  status?: string | null;
  end_date?: string | null;
}

const MANUAL_KIND_OPTIONS = [
  { value: 'morning_in', label: 'เข้างาน' },
  { value: 'evening_out', label: 'เลิกงาน' },
  { value: 'lunch_out', label: 'พักเที่ยง' },
  { value: 'lunch_in', label: 'กลับจากพัก' },
] as const;
type ManualKind = (typeof MANUAL_KIND_OPTIONS)[number]['value'];

function todayThai(): string {
  return new Date(Date.now() + 7 * 3_600_000).toISOString().slice(0, 10);
}

/** HR ลงเวลาย้อนหลังให้พนักงานที่ลืมสแกน (เขียนทับรอบนั้นของวันนั้น มี audit + แจ้งพนักงาน) */
function ManualEntryCard({ empCode, empName }: { empCode: string; empName: string }) {
  const toast = useToast();
  const consequence = useConsequence();
  const save = useManualAttendanceEntry();

  const maxDate = todayThai();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(maxDate);
  const [kind, setKind] = useState<ManualKind>('morning_in');
  const [time, setTime] = useState('');
  const [reason, setReason] = useState('');
  // fb#561 — พาร์ทไทม์ที่สแกนไม่ได้ (มือถือเชื่อม LINE ไม่ได้) HR จดวันมาทำงานไว้แล้ว → ลงทีเดียวหลายวัน
  // ยิง manual-entry เดิมทีละรอบ (ด่านงวดปิด/วันอนาคต/audit อยู่ฝั่ง server ครบ)
  const [mode, setMode] = useState<'single' | 'days'>('single');
  const [days, setDays] = useState<string[]>([]);
  const [pickDay, setPickDay] = useState('');
  const [inTime, setInTime] = useState('');
  const [outTime, setOutTime] = useState('');
  const [progress, setProgress] = useState<string | null>(null);

  function addDay() {
    if (progress) return; // ระหว่างบันทึกห้ามแก้รายการ — ลูปใช้รายการตอนกดยืนยัน
    if (!pickDay) return toast.error('เลือกวันที่ก่อนกดเพิ่ม');
    if (pickDay > maxDate) return toast.error('ลงวันล่วงหน้าไม่ได้');
    if (days.includes(pickDay)) return toast.error('วันนี้อยู่ในรายการแล้ว');
    setDays([...days, pickDay].sort());
    setPickDay('');
  }

  async function submitDays() {
    if (progress || save.isPending) return;
    if (days.length === 0) return toast.error('เพิ่มวันที่มาทำงานอย่างน้อย 1 วัน');
    if (!inTime || !outTime) return toast.error('ระบุเวลาเข้างานและเลิกงาน');
    if (outTime <= inTime) return toast.error('เวลาเลิกงานต้องหลังเวลาเข้างาน');
    if (!reason.trim()) return toast.error('ต้องระบุเหตุผล — จะถูกบันทึกในประวัติการแก้ไข');
    const ok = await consequence({
      title: `ลงวันมาทำงาน ${days.length} วัน`,
      intro: 'เมื่อกดยืนยัน ระบบจะบันทึกเวลาเข้า-เลิกงานของทุกวันในรายการแทนการสแกนจริง',
      lines: [
        { label: 'พนักงาน', value: empName },
        { label: 'วันที่', value: days.map((d) => thaiDateShort(d)).join(', '), emphasis: true },
        { label: 'เวลา', value: `เข้า ${inTime} น. · เลิก ${outTime} น. (ทุกวัน)` },
      ],
      effects: [
        'แต่ละวันนับเป็นวันมาทำงาน ใช้คิดค่าแรงเหมือนการสแกนจริง (ระบบติดป้ายว่าเป็นเวลาที่ลงย้อนหลัง)',
        'วันไหนมีเวลาเข้า/เลิกงานอยู่แล้ว จะถูกแทนด้วยเวลานี้',
        'บันทึกในประวัติการใช้งานระบบว่าใครลง ลงเมื่อไร (พนักงานที่ผูก LINE ได้รับแจ้ง)',
        'ต้องรัน "สรุปเวลาเข้างาน" แล้วรันร่างเงินเดือนใหม่ ยอดจึงจะขึ้น',
      ],
      confirmText: 'ยืนยันลงวันทำงาน',
    });
    if (!ok) return;
    const done: string[] = [];
    let inSaved = false; // วันที่ล้มลงเวลาเข้างานไปแล้วหรือยัง (reconcile นับวันจากเวลาเข้างาน)
    try {
      for (const d of days) {
        setProgress(`กำลังบันทึก ${done.length + 1}/${days.length}…`);
        inSaved = false;
        await save.mutateAsync({ emp_code: empCode, date: d, kind: 'morning_in', time: inTime, reason: reason.trim() });
        inSaved = true;
        await save.mutateAsync({ emp_code: empCode, date: d, kind: 'evening_out', time: outTime, reason: reason.trim() });
        done.push(d);
      }
      toast.success(`ลงวันมาทำงานแล้ว ${done.length} วัน — รันสรุปเวลาเข้างานแล้วรันร่างเงินเดือนใหม่`);
      setDays([]); setReason('');
    } catch (e) {
      const failed = days[done.length];
      setDays(days.slice(done.length));
      toast.error(
        `บันทึกแล้ว ${done.length} วัน · หยุดที่ ${failed ? thaiDateShort(failed) : '-'}${inSaved ? ' (ลงเวลาเข้างานแล้ว ยังขาดเวลาเลิกงาน)' : ''}: ${e instanceof Error ? e.message : 'ลงเวลาไม่สำเร็จ'} (วันที่ยังไม่บันทึกยังอยู่ในรายการ)`,
      );
    } finally {
      setProgress(null);
    }
  }

  async function submit() {
    if (!date) return toast.error('เลือกวันที่');
    if (!time) return toast.error('ระบุเวลา');
    if (!reason.trim()) return toast.error('ต้องระบุเหตุผล — จะถูกบันทึกในประวัติการแก้ไขและแจ้งพนักงาน');
    const kindLabel = MANUAL_KIND_OPTIONS.find((k) => k.value === kind)?.label ?? kind;
    const ok = await consequence({
      title: 'ลงเวลาย้อนหลัง',
      intro: 'เมื่อกดยืนยัน ระบบจะบันทึกเวลานี้แทนการสแกนจริง',
      lines: [
        { label: 'พนักงาน', value: empName },
        { label: 'วันที่', value: date },
        { label: `เวลา${kindLabel}`, value: `${time} น.`, emphasis: true },
      ],
      effects: [
        'เวลานี้ถูกใช้คำนวณเงินเดือน/สาย เหมือนการสแกนจริง (ระบบติดป้ายว่าเป็นเวลาที่ลงย้อนหลัง)',
        'พนักงานได้รับแจ้งทาง LINE ว่า HR ลงเวลาให้ พร้อมเหตุผล',
        'บันทึกในประวัติการใช้งานระบบว่าใครลง ลงเมื่อไร แก้จากค่าไหน',
        'ต้องรัน "สรุปเวลาเข้างาน" ของงวดนั้นใหม่ ตัวเลขจึงจะอัปเดต',
      ],
      confirmText: 'ยืนยันลงเวลา',
    });
    if (!ok) return;
    save.mutate(
      { emp_code: empCode, date, kind, time, reason: reason.trim() },
      {
        onSuccess: (r) => {
          toast.success(r.replaced_existing ? `แก้เวลา${kindLabel}ของวันนั้นแล้ว` : `ลงเวลา${kindLabel}แล้ว`);
          setTime(''); setReason('');
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'ลงเวลาไม่สำเร็จ'),
      },
    );
  }

  return (
    <div className="card p-3 mb-3">
      <button type="button" className="w-full flex items-center justify-between" onClick={() => setOpen((v) => !v)}>
        <span className="font-medium text-gray-900 flex items-center gap-1.5 text-sm">
          <History className="w-4 h-4 text-teal" /> ลงเวลาย้อนหลัง (พนักงานลืมสแกน)
        </span>
        <span className="text-xs text-gray-400">{open ? 'ซ่อน' : 'เปิดฟอร์ม'}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-gray-500">
            ใช้กรณีพนักงานลืมสแกนและไม่ได้ยื่นคำขอเอง — ถ้าพนักงานยื่นคำขอผ่านเมนู
            "รับรองเวลาเข้างาน" อยู่แล้ว ให้ไปอนุมัติที่หน้าอนุมัติแทน จะได้ไม่ซ้ำกัน
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className={mode === 'single' ? 'btn-primary' : 'btn-secondary'} disabled={!!progress || save.isPending} onClick={() => setMode('single')}>
              ทีละรอบ
            </button>
            <button type="button" className={mode === 'days' ? 'btn-primary' : 'btn-secondary'} disabled={!!progress || save.isPending} onClick={() => setMode('days')}>
              มาทำงานหลายวัน
            </button>
          </div>
          {mode === 'days' ? (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                สำหรับพนักงานที่สแกนไม่ได้ (เช่น มือถือเชื่อม LINE ไม่ได้) — ใส่วันที่มาทำงานทีละวันจนครบ
                แล้วกดบันทึกครั้งเดียว ระบบลงเวลาเข้า-เลิกงานให้ทุกวันในรายการ
              </p>
              <div>
                <label className="label">วันที่มาทำงาน</label>
                <div className="grid grid-cols-2 gap-3">
                  <input type="date" value={pickDay} max={maxDate} onChange={(e) => setPickDay(e.target.value)} className="input w-full" />
                  <button type="button" className="btn-secondary w-full" disabled={!!progress} onClick={addDay}>เพิ่มวัน</button>
                </div>
              </div>
              {days.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">รวม {days.length} วัน</div>
                  <div className="flex flex-wrap gap-1.5">
                    {days.map((d) => (
                      <span key={d} className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs whitespace-nowrap">
                        {thaiDateShort(d)}
                        <button type="button" aria-label={`เอา ${d} ออก`} className="text-gray-400 hover:text-red-600 px-1 disabled:opacity-40" disabled={!!progress} onClick={() => setDays(days.filter((x) => x !== d))}>×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">เวลาเข้างาน</label>
                  <input type="time" value={inTime} onChange={(e) => setInTime(e.target.value)} className="input w-full" />
                </div>
                <div>
                  <label className="label">เวลาเลิกงาน</label>
                  <input type="time" value={outTime} onChange={(e) => setOutTime(e.target.value)} className="input w-full" />
                </div>
              </div>
              <div>
                <label className="label">เหตุผล</label>
                <input
                  type="text"
                  value={reason}
                  maxLength={300}
                  placeholder="เช่น สแกนไม่ได้ มือถือเชื่อม LINE ไม่ได้ วันทำงานตามที่ HR จดไว้"
                  onChange={(e) => setReason(e.target.value)}
                  className="input w-full"
                />
              </div>
              <button type="button" className="btn-primary w-full" disabled={!!progress || save.isPending} onClick={submitDays}>
                {progress ?? `บันทึกวันมาทำงาน${days.length ? ` ${days.length} วัน` : ''}`}
              </button>
            </div>
          ) : (<>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">วันที่</label>
              <input type="date" value={date} max={maxDate} onChange={(e) => setDate(e.target.value)} className="input w-full" />
            </div>
            <div>
              <label className="label">รอบ</label>
              <select value={kind} onChange={(e) => setKind(e.target.value as ManualKind)} className="input w-full">
                {MANUAL_KIND_OPTIONS.map((k) => (
                  <option key={k.value} value={k.value}>{k.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="label">เวลา</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="input w-full" />
          </div>
          <div>
            <label className="label">เหตุผล (พนักงานเห็นข้อความนี้)</label>
            <input
              type="text"
              value={reason}
              maxLength={300}
              placeholder="เช่น ลืมสแกนเข้า มีลายเซ็นในใบลงเวลาสำรอง"
              onChange={(e) => setReason(e.target.value)}
              className="input w-full"
            />
          </div>
          <button type="button" className="btn-primary w-full" disabled={save.isPending} onClick={submit}>
            {save.isPending ? 'กำลังบันทึก…' : 'บันทึกเวลา'}
          </button>
          </>)}
        </div>
      )}
    </div>
  );
}

export function EmployeeAttendance() {
  const { data: employees } = useEmployees() as { data: EmpOpt[] | undefined };
  // วันปิดงวดของบริษัท — ใช้ตั้งช่วง "รอบจ่าย" (เช่น 26 ชน 25) ทั้งฝั่งไฟล์และฝั่งจอ
  const { data: timeSettings } = useTimeSettings();
  const cutoffDay = Number(timeSettings?.CUTOFF_DAY ?? 25) || 25;

  const [empCode, setEmpCode] = useState<string>('');
  const [query, setQuery] = useState<string>('');
  const [preset, setPreset] = useState<RangePreset>('cycle');
  const [range, setRange] = useState(() => rangeForPreset('cycle', cutoffDay));
  const { data, isLoading } = useEmployeeAttendance(empCode || null, range);
  const toast = useToast();
  const consequence = useConsequence();
  const overrideDay = useOverrideAttendanceDay();
  const amendOt = useAmendApprovedOt();
  // ยกเลิกใบลาที่อนุมัติแล้ว = HR/OWNER เท่านั้น (MANAGER เปิดหน้านี้ได้แต่ไม่เห็นปุ่ม — server กัน 403 อีกชั้น)
  const role = useAuthStore((st) => st.profile?.role);
  const canVoidLeave = role === 'OWNER' || role === 'HR';

  // fb#71 — วันที่มีใบลาเต็มวันแต่พนักงานมาสแกนจริง (TN EMP0123 15 ก.ย.) → HR ยกเลิกใบลาวันนั้น
  // ระบบจึงนับเป็นวันทำงาน + คืนสิทธิ์ลา (ต้องกดสรุปเวลาใหม่ ตัวเลขฝั่งเงินจึงเปลี่ยน)
  async function voidLeave(day: MyAttendanceDay) {
    if (overrideDay.isPending) return; // กันกดซ้ำระหว่างรอผล
    const empName = opts.find((o) => o.code === empCode)?.name ?? empCode;
    const ok = await consequence({
      title: 'ยกเลิกใบลา — พนักงานมาทำงานจริง',
      intro: 'เมื่อกดยืนยัน ใบลาของวันนี้จะถูกยกเลิก และวันนี้จะนับเป็นวันทำงานตามสแกนจริง',
      lines: [
        { label: 'พนักงาน', value: empName },
        { label: 'วันที่', value: day.date },
        { label: 'ใบลา', value: `${day.leave ? leaveLabel(day.leave.type) : '-'}: ${day.leave?.reason ?? '-'}`, emphasis: true },
      ],
      effects: [
        'สิทธิ์ลาของวันนี้ถูกคืนให้พนักงาน',
        'พนักงานที่ผูก LINE ไว้จะได้รับแจ้งว่า HR ยกเลิกใบลาวันนี้ให้เพราะมาทำงาน (ไม่ผูก = ไม่มีข้อความ)',
        'บันทึกในประวัติการใช้งานระบบว่าใครยกเลิก เมื่อไร',
        'ต้องรัน "สรุปเวลาเข้างาน" ของงวดนั้นใหม่ ตัวเลขฝั่งเงินเดือนจึงจะเปลี่ยน',
      ],
      confirmText: 'ยืนยันยกเลิกใบลา',
    });
    if (!ok) return;
    overrideDay.mutate(
      { emp_code: empCode, date: day.date, action: 'worked' },
      {
        onSuccess: (r) =>
          toast.success(
            `ยกเลิกใบลาแล้ว — วันนี้นับเป็นวันทำงาน อย่าลืมกดสรุปเวลาใหม่${r.employee_notified === false ? ' (พนักงานไม่ได้ผูก LINE จึงไม่มีข้อความแจ้ง)' : ''}`,
          ),
        onError: (e) => toast.error(e instanceof Error ? e.message : 'ยกเลิกใบลาไม่สำเร็จ'),
      },
    );
  }

  // fb#113 — ใบลาที่อนุมัติแล้วแต่ผิดประเภท (TN EMP0185 เลือกลาไม่รับเงินตอนยังไม่ตั้งสิทธิ์) → HR ยกเลิก
  // จากแถววันนั้นได้เลย (override-day action=void · server ตรวจงวดปิด/ลาย้อนหลัง strict อีกชั้น)
  async function cancelLeave(day: MyAttendanceDay, reason: string): Promise<boolean> {
    if (overrideDay.isPending || !day.leave?.leave_id) return false;
    if (reason.length < 5) {
      toast.error('ใส่เหตุผลอย่างน้อย 5 ตัวอักษร — จะบันทึกในประวัติและแจ้งพนักงาน');
      return false;
    }
    const empName = opts.find((o) => o.code === empCode)?.name ?? empCode;
    const ok = await consequence({
      title: 'ยกเลิกใบลา — ให้พนักงานยื่นใหม่',
      intro: 'เมื่อกดยืนยัน ใบลาใบนี้จะถูกยกเลิก วันนั้นจะไม่นับเป็นวันลาอีก จนกว่าพนักงานจะยื่นใบใหม่',
      lines: [
        { label: 'พนักงาน', value: empName },
        { label: 'วันที่', value: day.date },
        { label: 'ใบลา', value: `${leaveLabel(day.leave.type)} (${day.leave.leave_id})`, emphasis: true },
        { label: 'เหตุผล', value: reason },
      ],
      effects: [
        'พนักงานที่ผูก LINE ไว้จะได้รับข้อความพร้อมเหตุผล และให้ยื่นใบลาใหม่ในแอป',
        'ถ้าพนักงานไม่ยื่นใหม่และไม่มีสแกนวันนั้น ระบบจะนับเป็นขาดงาน',
        'บันทึกในประวัติการใช้งานระบบว่าใครยกเลิก เมื่อไร',
        'ยอดเงินเดือนจะเปลี่ยนเมื่อครบ 4 ขั้น: พนักงานยื่นใบใหม่ → อนุมัติใบใหม่ → รัน "สรุปเวลาเข้างาน" ใหม่ → รันร่างเงินเดือนใหม่',
      ],
      confirmText: 'ยืนยันยกเลิกใบลา',
    });
    if (!ok) return false;
    try {
      const r = await overrideDay.mutateAsync({ emp_code: empCode, date: day.date, action: 'void', leave_id: day.leave.leave_id, note: reason });
      toast.success(
        `ยกเลิกใบลาแล้ว — ต่อไป: พนักงานยื่นใหม่ → อนุมัติ → สรุปเวลาใหม่ → รันร่างเงินเดือนใหม่${r.employee_notified === false ? ' (พนักงานไม่ได้ผูก LINE จึงไม่มีข้อความแจ้ง ต้องบอกเอง)' : ''}`,
      );
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ยกเลิกใบลาไม่สำเร็จ');
      return false;
    }
  }

  // fb#83 — กดอนุมัติ OT ผิด (TN 27 ส.ค. อนุมัติ 09:00–18:30 ที่ขอจริง 18:00–18:30) → HR หดเวลา/ยกเลิกใบ
  async function amendOvertime(day: MyAttendanceDay, ot: OtOnDay, input: AmendOtInput): Promise<boolean> {
    if (amendOt.isPending) return false;
    if (!input.reason) {
      toast.error('ใส่เหตุผลก่อน — จะบันทึกในประวัติและแจ้งพนักงาน');
      return false;
    }
    const empName = opts.find((o) => o.code === empCode)?.name ?? empCode;
    const before = `${ot.start_time.slice(0, 5)}–${ot.end_time.slice(0, 5)} (${ot.hours} ชม.)`;
    const ok = await consequence({
      title: input.action === 'void' ? 'ยกเลิก OT ที่อนุมัติแล้ว' : 'แก้เวลา OT ที่อนุมัติแล้ว',
      intro: input.action === 'void'
        ? 'เมื่อกดยืนยัน ใบ OT นี้จะถูกยกเลิกทั้งใบ และไม่ถูกนำไปคิดเงิน'
        : 'เมื่อกดยืนยัน ระบบจะคิด OT ตามเวลาใหม่นี้แทนเวลาเดิม',
      lines: [
        { label: 'พนักงาน', value: empName },
        { label: 'วันที่', value: day.date },
        { label: 'เดิม', value: before },
        { label: 'ใหม่', value: input.action === 'void' ? 'ยกเลิก (0 ชม.)' : `${input.start_time}–${input.end_time}`, emphasis: true },
        { label: 'เหตุผล', value: input.reason },
      ],
      effects: [
        'พนักงานที่ผูก LINE ไว้จะได้รับแจ้งเวลาเดิม/ใหม่ พร้อมเหตุผล (ไม่ผูก = ไม่มีข้อความ)',
        'บันทึกในประวัติการใช้งานระบบว่าใครแก้ เมื่อไร เดิมเท่าไหร่',
        'ต้องรัน "สรุปเวลาเข้างาน" ของงวดนั้นใหม่ ตัวเลขฝั่งเงินเดือนจึงจะเปลี่ยน',
      ],
      confirmText: input.action === 'void' ? 'ยืนยันยกเลิก OT' : 'ยืนยันแก้เวลา OT',
    });
    if (!ok) return false;
    try {
      const r = await amendOt.mutateAsync({
        id: ot.id,
        action: input.action,
        reason: input.reason,
        ...(input.action === 'amend' ? { start_time: input.start_time, end_time: input.end_time } : {}),
      });
      toast.success(
        `${input.action === 'void' ? 'ยกเลิก OT แล้ว' : `แก้ OT แล้ว ${r.hours_before} → ${r.hours_after} ชม.`} — อย่าลืมกดสรุปเวลาใหม่${r.employee_notified === false ? ' (พนักงานไม่ได้ผูก LINE จึงไม่มีข้อความแจ้ง)' : ''}`,
      );
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'แก้ OT ไม่สำเร็จ');
      return false;
    }
  }

  // ค่า cutoff มาจาก API (ทีหลัง) — พอมาถึงต้องขยับช่วงให้ตรงรอบจริง ไม่งั้นค้างที่
  // ค่าเดาไว้ 25 ของบริษัทที่ปิดงวดวันอื่น. บริษัทที่ปิดงวดตรงสิ้นเดือนไม่มีปุ่ม
  // "รอบจ่ายนี้" ให้กด → ถอยไปที่ "เดือนนี้" ไม่งั้นจะไม่มีปุ่มไหนถูกไฮไลต์เลย
  const [syncedCutoff, setSyncedCutoff] = useState<number | null>(null);
  if (timeSettings && syncedCutoff !== cutoffDay) {
    setSyncedCutoff(cutoffDay);
    const p: RangePreset =
      preset === 'cycle' && isCalendarMonthCycle(cutoffDay, todayBkk().slice(0, 7)) ? 'this-month' : preset;
    if (p !== preset) setPreset(p);
    if (p !== 'custom') setRange(rangeForPreset(p, cutoffDay));
  }

  function applyPreset(p: RangePreset) {
    setPreset(p);
    if (p !== 'custom') setRange(rangeForPreset(p, cutoffDay));
  }

  // fb#313 — คนที่ลาออก/เลิกจ้างแล้วยังเปิดดูประวัติได้ (HR ต้องดูงวดสุดท้าย) ต่อท้ายรายการพร้อมป้ายวันออก
  const isExited = (e: EmpOpt) => e.status === 'resigned' || e.status === 'terminated';
  const opts = (employees ?? [])
    .filter((e) => e.status === 'active' || e.status === 'probation' || isExited(e))
    .sort((a, b) => Number(isExited(a)) - Number(isExited(b)))
    .map((e) => {
      const name =
        `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() +
          (e.nickname ? ` (${e.nickname})` : '') || e.emp_code;
      const exited = isExited(e)
        ? ` · ${e.status === 'resigned' ? 'ลาออกแล้ว' : 'เลิกจ้างแล้ว'}${e.end_date ? ` ${thaiDateShort(e.end_date)}` : ''}`
        : '';
      return { code: e.emp_code, name, label: `${name} · ${e.emp_code}${exited}` };
    });

  // พิมพ์ชื่อ/ชื่อเล่น/รหัส แล้วเลือก — จับคู่ label เต็ม, ชื่อ, หรือรหัส (ตัวพิมพ์เล็ก)
  function resolveEmp(typed: string) {
    setQuery(typed);
    const q = typed.trim().toLowerCase();
    const hit = opts.find(
      (o) =>
        o.label.toLowerCase() === q ||
        o.code.toLowerCase() === q ||
        o.name.toLowerCase() === q,
    );
    setEmpCode(hit ? hit.code : '');
  }

  return (
    <AdminLayout title="ประวัติเข้างานรายคน">
      <AttendanceExportCard cutoffDay={cutoffDay} />
      <div className="card p-3 mb-3">
        <label className="label">พิมพ์ชื่อหรือรหัสพนักงาน</label>
        <input
          className="input"
          list="emp-att-list"
          value={query}
          onChange={(e) => resolveEmp(e.target.value)}
          placeholder="พิมพ์ชื่อ / ชื่อเล่น / รหัส แล้วเลือกจากรายการ"
          autoComplete="off"
        />
        <datalist id="emp-att-list">
          {opts.map((o) => (
            <option key={o.code} value={o.label} />
          ))}
        </datalist>
        {query && !empCode && (
          <p className="text-xs text-amber-600 mt-1">ยังไม่ตรงกับพนักงานคนไหน — เลือกจากรายการที่ขึ้นมา</p>
        )}
      </div>

      {!empCode ? (
        <div className="card p-6 text-center text-gray-400">
          <p className="text-4xl mb-2">👤</p>
          <p className="text-sm">เลือกพนักงานเพื่อดูประวัติการเข้างาน</p>
        </div>
      ) : (
        <>
          <ManualEntryCard
            empCode={empCode}
            empName={opts.find((o) => o.code === empCode)?.name ?? empCode}
          />
          <AttendanceRangePresets
            preset={preset}
            onChange={applyPreset}
            cutoffDay={cutoffDay}
            range={range}
            onRangeChange={setRange}
          />
          <AttendanceTimeline
            data={data}
            isLoading={isLoading}
            onVoidLeave={canVoidLeave ? voidLeave : undefined}
            onCancelLeave={canVoidLeave ? cancelLeave : undefined}
            onAmendOt={canVoidLeave ? amendOvertime : undefined}
          />
        </>
      )}
    </AdminLayout>
  );
}
